import React, { useEffect, useRef } from "react";
import L from "leaflet";
import { MapPin } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const CITIES = [
  { key: "torrevieja", name: "Torrevieja", coords: [37.9787, -0.6822] },
  { key: "punta-prima", name: "Punta Prima", coords: [37.9333, -0.7167] },
  { key: "orihuela-costa", name: "Orihuela Costa", coords: [37.9139, -0.7481] },
  { key: "los-altos", name: "Los Altos", coords: [37.9550, -0.7222] },
  { key: "san-miguel", name: "San Miguel de Salinas", coords: [37.9744, -0.7867] },
  { key: "guardamar", name: "Guardamar del Segura", coords: [38.0885, -0.6544] },
  { key: "la-marina", name: "La Marina", coords: [38.1275, -0.6489] },
];

export function ServiceArea() {
  const { t } = useLanguage();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const map = L.map(mapRef.current, {
      center: [38.02, -0.7],
      zoom: 10,
      scrollWheelZoom: false,
      zoomControl: true,
      attributionControl: true,
    });
    mapInstanceRef.current = map;

    L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
      {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> · &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: "abcd",
        maxZoom: 19,
      }
    ).addTo(map);

    const latlngs = CITIES.map((c) => c.coords);

    // Compute a rough hull-ish outline by ordering points around centroid
    const cx =
      latlngs.reduce((s, p) => s + p[0], 0) / latlngs.length;
    const cy =
      latlngs.reduce((s, p) => s + p[1], 0) / latlngs.length;
    const ordered = [...latlngs].sort(
      (a, b) => Math.atan2(a[0] - cx, a[1] - cy) - Math.atan2(b[0] - cx, b[1] - cy)
    );

    L.polygon(ordered, {
      color: "#D36135",
      weight: 2,
      opacity: 0.85,
      fillColor: "#D36135",
      fillOpacity: 0.12,
      dashArray: "4 6",
    }).addTo(map);

    CITIES.forEach((c) => {
      const icon = L.divIcon({
        className: "",
        html: `<div class="custom-pin">${c.name.charAt(0)}</div>`,
        iconSize: [34, 34],
        iconAnchor: [17, 17],
      });
      L.marker(c.coords, { icon })
        .addTo(map)
        .bindTooltip(c.name, {
          permanent: true,
          direction: "top",
          offset: [0, -14],
          className:
            "!bg-white !border !border-brand-sand !text-brand-ink !font-semibold !rounded-full !px-2 !py-0.5 !shadow-sm",
        });
    });

    map.fitBounds(latlngs, { padding: [50, 50] });

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  return (
    <section
      id="area"
      data-testid="service-area-section"
      className="relative py-24 md:py-32 bg-gradient-to-b from-brand-cream to-brand-creamSoft"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-terracotta">
              {t("area.eyebrow")}
            </p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-brand-ink leading-tight">
              {t("area.title")}
            </h2>
            <p className="mt-5 text-base text-brand-ink/70 leading-relaxed">
              {t("area.subtitle")}
            </p>

            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {CITIES.map((c, idx) => (
                <li
                  key={c.key}
                  data-testid={`city-item-${c.key}`}
                  className="flex items-center gap-3 bg-white border border-brand-sand rounded-2xl p-3 pr-4"
                >
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-terracotta text-white font-semibold text-sm">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base font-semibold text-brand-ink truncate">
                      {c.name}
                    </div>
                  </div>
                  <MapPin size={16} className="text-brand-aegean shrink-0" />
                </li>
              ))}
            </ul>

            <div className="mt-6 inline-flex items-start gap-2 rounded-2xl bg-brand-aegean/8 border border-brand-aegean/20 px-4 py-3">
              <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-brand-terracotta" />
              <p className="text-sm text-brand-aegean font-medium">{t("area.note")}</p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative rounded-3xl overflow-hidden border border-brand-sand shadow-xl shadow-brand-aegean/10 bg-white">
              <div
                ref={mapRef}
                data-testid="leaflet-map"
                className="w-full h-[560px] md:h-[640px]"
              />
              <div className="absolute top-4 left-4 z-[500] pointer-events-none">
                <div className="bg-white/95 backdrop-blur px-3 py-1.5 rounded-full text-xs font-semibold text-brand-aegean border border-brand-sand shadow-sm">
                  Costa Blanca Sur · ES
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
